import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ImageCrop, { canCrop } from './ImageCrop';

// ── Chat pictures (report 42100db4, Calem: "Allow users and groups to have a profile picture in chat") ─────────────
// Asked, he answered that a person's picture is theirs to set or an administrator's, a group's an administrator's
// alone, and that they show in the conversation list and at the head of an open chat only — never beside each message.
// The lists carry only a fingerprint of each picture (`avatar` on a chat); the picture itself — a 128px JPEG kept on
// the server — is asked for once per fingerprint and held for the life of the tab, so the list's 5-second poll never
// fetches a picture it has already drawn. A picture is chosen with the file picker, cut square in the crop window
// (ImageCrop's `avatar`), shrunk here and only then sent.

export type PicKind = 'user' | 'group';
const SIDE = 128;          // the pixels a picture is kept at: twice the largest it is drawn, so a phone's screen stays sharp
const QUALITY = 0.85;

// What this tab has drawn, keyed 'user:<id>' / 'group:<id>': the fingerprint and the picture it belongs to — or, while
// it is still being asked for, the question itself, so two rows showing one person ask once.
type Held = { v: string; url: string; p: Promise<string> | null };
const held = new Map<string, Held>();

/** Put a picture this tab has just sent where the rows look, so it shows at once without being asked for again. */
export function keepPic(kind: PicKind, id: string, v: string, url: string): void {
  if (!id) return;
  if (v && url) held.set(`${kind}:${id}`, { v, url, p: null });
  else held.delete(`${kind}:${id}`);
}

/** The picture behind a fingerprint, as a data URL; '' while there is none, or while it is still on its way. */
export function usePic(kind: PicKind, id: string, v: string): string {
  const key = `${kind}:${id}`;
  const [got, setGot] = useState<{ key: string; v: string; url: string }>(() => {
    const h = held.get(key);
    return { key, v, url: v && h && h.v === v && !h.p ? h.url : '' };
  });
  useEffect(() => {
    if (!id || !v) return;
    let h = held.get(key);
    if (!h || h.v !== v) {
      const p: Promise<string> = api.chatPicture(kind, id).then(r => String((r && r.url) || ''), () => '');
      h = { v, url: '', p };
      held.set(key, h);
      p.then(url => {
        const now = held.get(key);
        if (now && now.p === p) {
          if (url) held.set(key, { v, url, p: null });
          else held.delete(key);   // nothing came back: the next time this chat is drawn asks again
        }
      });
    }
    if (!h.p) { setGot({ key, v, url: h.url }); return; }
    let live = true;
    h.p.then(url => { if (live) setGot({ key, v, url }); });
    return () => { live = false; };
  }, [key, kind, id, v]);
  return v && got.key === key && got.v === v ? got.url : '';
}

/** A chat's picture in its circle — or `fallback` (the row's old icon, or nothing) while there is none. */
export function ChatAvatar({ kind, id, v, size, fallback = null, style }: {
  kind: PicKind; id: string; v?: string; size: number | string; fallback?: React.ReactNode; style?: React.CSSProperties;
}) {
  const url = usePic(kind, id, v || '');
  if (!url) return <>{fallback}</>;
  return (
    <img src={url} alt="" draggable={false}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, display: 'block', background: 'var(--c-gray-100)', ...style }} />
  );
}

/** The cut-out square, shrunk to SIDE pixels and written as a JPEG data URL — about 10 KB, what the server keeps. */
async function toAvatar(blob: Blob): Promise<string> {
  const src = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((ok, no) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => no(new Error('unreadable'));
      i.src = src;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!(w > 0) || !(h > 0)) throw new Error('unreadable');
    const s = Math.min(w, h);   // a picture a pixel out of square is cut to its middle
    const c = document.createElement('canvas');
    c.width = SIDE;
    c.height = SIDE;
    const g = c.getContext('2d');
    if (!g) throw new Error('no canvas');
    g.fillStyle = '#fff';       // a PNG's see-through parts come out white in a JPEG, not black
    g.fillRect(0, 0, SIDE, SIDE);
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, (w - s) / 2, (h - s) / 2, s, s, 0, 0, SIDE, SIDE);
    return c.toDataURL('image/jpeg', QUALITY);
  } finally {
    URL.revokeObjectURL(src);
  }
}

/** Everything a picture door needs but its own buttons: the hidden file picker, the square crop, the shrinking and the
 *  sending. `save` and `drop` do the asking and throw when it fails; what went wrong is handed to `onError` ('' clears
 *  it). `pick` opens the file picker, `remove` takes the picture away, and `node` is to be drawn wherever the door is. */
export function usePicDoor(save: (url: string) => Promise<void>, drop: () => Promise<void>, onError: (msg: string) => void) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const pick = () => {
    if (busy || !input.current) return;
    input.current.value = '';
    input.current.click();
  };
  const chosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!canCrop(f.type, f.name)) { onError(t('chat_pic_bad')); return; }
    onError('');
    setFile(f);
  };
  const use = async (cut: Blob) => {
    setFile(null);
    setBusy(true);
    onError('');
    try {
      let url = '';
      try { url = await toAvatar(cut); } catch { onError(t('chat_pic_bad')); return; }
      try { await save(url); } catch { onError(t('chat_pic_failed')); }
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    onError('');
    try { await drop(); } catch { onError(t('chat_pic_failed')); } finally { setBusy(false); }
  };
  const node = (
    <>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={chosen} style={{ display: 'none' }} />
      {file && <ImageCrop blob={file} name={file.name} avatar onCancel={() => setFile(null)} onCrop={cut => void use(cut)} />}
    </>
  );
  return { pick, remove, busy, node };
}

/** A picture door as one button: My picture beside the bell, Group picture beside Members. With no picture it opens the
 *  file picker; with one it opens a small menu — Change, Remove — which Escape or a press anywhere else shuts. The
 *  button wears the picture itself while there is one. */
export function PicButton({ pic, label, title, save, drop, onError, style, showLabel = true }: {
  pic: string; label: string; title: string; save: (url: string) => Promise<void>; drop: () => Promise<void>;
  onError: (msg: string) => void; style: React.CSSProperties; showLabel?: boolean;
}) {
  const { t } = useTranslation();
  const door = usePicDoor(save, drop, onError);
  const [menu, setMenu] = useState(false);
  const wrap = useRef<HTMLSpanElement | null>(null);
  // Escape is caught on the way down and stopped there, so it shuts this menu and not the chat window under it.
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; e.stopPropagation(); setMenu(false); };
    const away = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setMenu(false); };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', away);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('mousedown', away); };
  }, [menu]);
  const item: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '7px 10px', fontSize: 12.5, border: 'none', borderRadius: 6,
    background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
  };
  return (
    <span ref={wrap} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button onClick={() => { if (door.busy) return; if (pic) setMenu(m => !m); else door.pick(); }} disabled={door.busy}
        title={title} aria-label={label} aria-haspopup={pic ? 'menu' : undefined} aria-expanded={pic ? menu : undefined}
        style={{ ...style, cursor: door.busy ? 'wait' : 'pointer' }}>
        {door.busy
          ? <i className="ti ti-loader" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} aria-hidden />
          : pic
            ? <img src={pic} alt="" draggable={false} style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            : <i className="ti ti-user-circle" style={{ fontSize: 15 }} aria-hidden />}
        {showLabel && label}
      </button>
      {menu && pic && (
        <div data-pic-menu role="menu"
          style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 30, minWidth: 150, padding: 4, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)' }}>
          <button role="menuitem" onClick={() => { setMenu(false); door.pick(); }} style={item}>
            <i className="ti ti-photo" style={{ fontSize: 14 }} aria-hidden />{t('chat_pic_change')}
          </button>
          <button role="menuitem" onClick={() => { setMenu(false); void door.remove(); }} style={{ ...item, color: '#E24B4A' }}>
            <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />{t('chat_pic_remove')}
          </button>
        </div>
      )}
      {door.node}
    </span>
  );
}
